import './index.scss'
import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Box, Stepper, Step, StepLabel, Button, StepIcon } from '@mui/material';
import { styled } from '@mui/system';
import Loading from '../../components/common/loading.js';
import { useTranslation } from 'react-i18next';
import ClientInformation from '../taxQuestionnaire/clientInformation/index.js';
import RefundDetails from '../taxQuestionnaire/refundDetails/index.js';
import Submission from '../taxQuestionnaire/submission/index.js';
import TravelHistory from '../taxQuestionnaire/travelHistory/index.js';
import TaxSummary from '../taxQuestionnaire/taxSummary/index.js';
import { userInfo } from '../../redux/slice';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { get, post } from '../../util/request';
import { Form } from 'antd';
import BankAccountSubmitSuccessPopUp from '../bankAccount/bankAccountSubmitSuccessPopUp';

export default TaxFilingQuestionnaire; function TaxFilingQuestionnaire() {
    const { t } = useTranslation();
    const user = useSelector(userInfo);
    const [activeStep, setActiveStep] = useState(0);
    const initialSteps = sessionStorage.getItem('taxAction') === 'Pay' ? ['Submission'] : ['Client Information', 'Refund Details', 'US Immigration & Travel', 'Submission'];
    const [steps, setSteps] = useState(initialSteps);
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [taxFilingQuestionnaire, setTaxFilingQuestionnaire] = useState({});
    const [country, setCountry] = useState([]);
    const [eligible, setEligible] = useState(true);
    const [nextButtonStatus, setNextButtonStatus] = useState(false);
    const formRef = useRef();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [modalProperty, setModalProperty] = useState({ isModalOpen: false, title: '', content: '', icon: '' });
    const history = useHistory();

    //Stepper Design
    const CustomStepIcon = styled(StepIcon)(({ theme }) => ({
        width: 37,
        height: 37,
        '& .MuiStepIcon-circle': {
            width: 37,
            height: 37,
            borderRadius: '50%'
        },
    }));

    //Set up
    const setup = async () => {
        let params = {
            accessToken: user.accessToken,
            userId: user.userId,
            year: sessionStorage.getItem('taxYear'),
            clientId: user.clientID
        }
        await get('/taxQuestionnaire/detail', params).then((data) => {
            var day = data?.DateOfBirth.slice(8, 10)
            var year = data?.DateOfBirth.slice(0, 4)
            var month = data?.DateOfBirth.slice(5, 7)
            data.DateOfBirth = year + ' / ' + month + ' / ' + day;
            if (data.TaxMailingCountry === '') {
                data.TaxMailingCountry = user.country;
            }

            setTaxFilingQuestionnaire(data);
            formRef.current?.setFieldsValue(data);

            if (data.Status === 'Submitted' && data.TotalTaxFilingFeeShortfall > 0) {
                setActiveStep(steps.indexOf('Submission'));
            }

            setLoading(false);

        }).catch(() => {
            setLoading(false);
        });
    }

    const getCountryOption = async () => {
        let params = {
            accessToken: user.accessToken,
            userId: user.userId
        };
        const data = await get('/appUser/profile/country', params);
        if (data) {
            if (data.length > 0) {
                setCountry(data);
            }
        }
    };

    useEffect(() => {
        getCountryOption();
        setup();
    }, []);

    useEffect(() => {
        getCountryOption();
    }, [window.localStorage.lang]);


    const handleStep = (step) => {
        if (completedSteps.has(step) && !(activeStep === steps.length)) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            setActiveStep(step);
        }
    };

    const handleBack = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleNext = () => {
        if (taxFilingQuestionnaire.Status === '' || taxFilingQuestionnaire?.Status?.toLowerCase() === 'new') {
            formRef?.current
                ?.validateFields()
                .then(() => {
                    if (!error) {
                        if (activeStep < steps.length) {
                            setLoading(true);
                            handleSubmit();
                        }
                    } else {
                        const errorList = document.querySelectorAll(".error-message")
                        errorList[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }
                })
                .catch((err) => {
                    const errorList = document.querySelectorAll(".ant-form-item-has-error")
                    errorList[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                });
        }
        else {
            handleNextStep();
        }
    };

    const handleNextStep = async () => {
        if (((activeStep === steps.length - 1) && !(taxFilingQuestionnaire.Status === '' || taxFilingQuestionnaire?.Status?.toLowerCase() === 'new'))) {
            goto();
        }
        else {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            const newCompletedSteps = new Set(completedSteps);
            newCompletedSteps.add(activeStep);
            setCompletedSteps(newCompletedSteps);
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    }

    const handleSubmit = async () => {
        const tab = steps[activeStep];
        const detail = formRef.current?.getFieldsValue();
        detail.TaxFilingYear = taxFilingQuestionnaire.TaxFilingYear;
        detail.CustomerID = taxFilingQuestionnaire.CustomerID;

        detail.Tab = tab;
        if (tab === 'US Immigration & Travel') {
            detail.USStays = taxFilingQuestionnaire.USStays;
            detail.PeriodStayedInUSCurrentYear = taxFilingQuestionnaire.PeriodStayedInUSCurrentYear;
            detail.PeriodStayedInUSLastYear = taxFilingQuestionnaire.PeriodStayedInUSLastYear;
            detail.PeriodStayedInUSTwoYearsAgo = taxFilingQuestionnaire.PeriodStayedInUSTwoYearsAgo;
        }
        else if (tab === 'Submission') {
            detail.SignatureFile = taxFilingQuestionnaire.SignatureFile;
        }

        const params = {
            ...detail,
            accessToken: user.accessToken,
            userId: user.userId
        }

        await post('/taxQuestionnaire/update', params).then((data) => {
            if (data.message === 'Success') {
                handleNextStep();
            }
            else {
                setModalProperty({
                    isModalOpen: true,
                    icon: 'submit-error',
                    title: 'Error',
                    content: 'Error in submission. Please try again.'
                });
                setLoading(false);
            }
        }).catch(() => {
            setModalProperty({
                isModalOpen: true,
                icon: 'submit-error',
                title: 'Error',
                content: 'Error in submission. Please try again.'
            });
            setLoading(false);
        });
        setLoading(false);
    }

    useEffect(() => {
        if (activeStep === steps.indexOf('Submission')) {
            if (taxFilingQuestionnaire.Status === '' || taxFilingQuestionnaire?.Status?.toLowerCase() === 'new') {
                setNextButtonStatus(true);
            }
            else {
                setNextButtonStatus(false);
            }

        }
        else {
            setNextButtonStatus(false);
        }
    }, [activeStep, steps])

    const handleSignatureChange = async (status, image) => {
        setNextButtonStatus(status);
        if (!status) {
            handleChangeTaxFilingQuestionnaire('SignatureFile', image);
        }
    }

    const handleClientInformationChange = async (clientInfo) => {
        if (clientInfo.taxResidency === 'USA' || clientInfo.otherSourceOfIncome === 'Yes') {
            setSteps(['Client Information']);
            setEligible(false);
        }
        else {
            setSteps(initialSteps);
            setEligible(true);
        }
    }


    const handleTravelDateChange = async (total, validTravelDates) => {
        if (total.SPTDays >= 183) {
            setSteps(['Client Information', 'Refund Details', 'US Immigration & Travel']);
            setEligible(false);
        }
        else {
            setSteps(initialSteps);
            setEligible(true);
        }

        setTaxFilingQuestionnaire(prevData => ({
            ...prevData,
            PeriodStayedInUSCurrentYear: total.periodStayCurrentYear,
            PeriodStayedInUSLastYear: total.periodStayLastYear,
            PeriodStayedInUSTwoYearsAgo: total.periodStayLast2Years,
            USStays: validTravelDates
        }));
    }

    const handleChangeTaxFilingQuestionnaire = async (key, value) => {
        setTaxFilingQuestionnaire(prevData => ({
            ...prevData,
            [key]: value
        }));
        formRef?.current.setFieldValue(key, value);

        if (key === 'ActiveGreenCardHolder' && value === 'Yes') {
            setSteps(['Client Information', 'Refund Details', 'US Immigration & Travel']);
            setEligible(false);
        }
        else {
            setSteps(initialSteps);
            setEligible(true);
        }
    }

    useEffect(() => {

    }, [taxFilingQuestionnaire])


    const goto = () => {
        history.push({
            pathname: user.sfUserType === 'CL' ? '/questionnaires' : '/preview/questionnaires'
        });
    }



    return (<>
        {
            user.sfUserType === 'CL' && (<>
                {loading && <Loading />}
                <BankAccountSubmitSuccessPopUp isModalOpen={modalProperty.isModalOpen} icon={modalProperty.icon} title={t(modalProperty.title)} content={t(modalProperty.content)} handleOk={goto} />
                <div className='tax-filing-questionnaire-container'>
                    <div className='main-box stepper'>
                        <Form autoComplete="off" ref={formRef} scrollToFirstError>
                            <Box sx={{ width: '1446px' }}>
                                <Stepper activeStep={activeStep} alternativeLabel>
                                    {steps.map((label, index) => {
                                        const stepProps = {};
                                        const labelProps = {};

                                        if (completedSteps.has(index) && activeStep !== index) {
                                            stepProps.completed = true;
                                        }


                                        return (
                                            <Step key={label} {...stepProps} onClick={() => handleStep(index)}>
                                                <StepLabel {...labelProps} StepIconComponent={index === activeStep ? CustomStepIcon : null}>{t(label)}</StepLabel>
                                            </Step>
                                        );
                                    })}
                                </Stepper>
                                {//Summary Tab
                                    activeStep === steps.length ? (
                                        <>
                                            {
                                                steps.length > 0 ? (<>
                                                    <React.Fragment>
                                                        <div className='content'>
                                                            <TaxSummary Eligible={eligible} />
                                                        </div>
                                                    </React.Fragment>
                                                </>) : <Loading />
                                            }
                                        </>
                                    ) : (
                                        <React.Fragment>
                                            <div className='content'>
                                                {//Client Information
                                                    activeStep === steps.indexOf('Client Information') && (<>
                                                        <ClientInformation Country={country} TaxFilingQuestionnaire={taxFilingQuestionnaire} HandleClientInformationSave={handleClientInformationChange} HandleTaxFilingQuestionnaireSave={handleChangeTaxFilingQuestionnaire} />
                                                    </>)
                                                }
                                                {//Tax Filing Fees
                                                    activeStep === steps.indexOf('Refund Details') && (<>
                                                        <RefundDetails TaxFilingQuestionnaire={taxFilingQuestionnaire} Country={country} />
                                                    </>)
                                                }
                                                {//Travel History
                                                    activeStep === steps.indexOf('US Immigration & Travel') && (<>
                                                        <TravelHistory HandleTravelDateChange={handleTravelDateChange} TaxFilingQuestionnaire={taxFilingQuestionnaire} HandleTaxFilingQuestionnaireSave={handleChangeTaxFilingQuestionnaire} />
                                                    </>)
                                                }
                                                {//Methods to receive tax refund
                                                    activeStep === steps.indexOf('Submission') && (<>
                                                        <Submission handleSignatureSave={handleSignatureChange} TaxFilingQuestionnaire={taxFilingQuestionnaire} />
                                                    </>)
                                                }

                                            </div>
                                            <div className='footer'>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                                                    {
                                                        sessionStorage.getItem('taxAction') !== 'Pay' && (<>
                                                            <Button
                                                                color="inherit"
                                                                disabled={activeStep === 0}
                                                                onClick={handleBack}
                                                                sx={{ mr: 1 }}
                                                                id='back-button'
                                                            >
                                                                {t('BACK')}
                                                            </Button>
                                                        </>)
                                                    }
                                                    <Box sx={{ flex: '1 1 auto' }} />
                                                    {
                                                        sessionStorage.getItem('taxAction') === 'Pay' ? (<>
                                                            <Button onClick={goto} id='next-button' className={activeStep === steps.length - 1 ? "confirm-button" : ""}>
                                                                {t('Close')}
                                                            </Button>
                                                        </>) : (<>
                                                            <Button htmlType={"submit"} onClick={handleNext} id='next-button' className={activeStep === steps.length - 1 ? "confirm-button" : ""} disabled={nextButtonStatus}>
                                                                {activeStep === steps.length - 1 ? eligible ? (taxFilingQuestionnaire?.Status?.toLowerCase() === 'new' || taxFilingQuestionnaire.Status === '') ? t('CONFIRM AND SIGN') : t('CONFIRM') : t('CONFIRM') : t('NEXT')}
                                                            </Button>
                                                        </>)
                                                    }
                                                </Box>
                                            </div>
                                        </React.Fragment>
                                    )}
                            </Box>
                        </Form>
                    </div>
                </div>
            </>)
        }
    </>);
};